import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const comeFunzionaJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      '@id': 'https://itaobuyspread.top/come-funziona-itaobuy-spreadsheet/#article',
      headline: 'Come Funziona Itaobuy Spreadsheet: Guida Passo Dopo Passo',
      description: 'Tutorial passo dopo passo su come utilizzare Itaobuy Spreadsheet per trovare e acquistare i migliori prodotti di moda in modo semplice e sicuro.',
      image: 'https://readdy.ai/api/search-image?query=Step%20by%20step%20tutorial%20concept%20on%20laptop%20screen%2C%20warm%20natural%20lighting%2C%20hands%20typing%20on%20keyboard%2C%20organized%20modern%20Italian%20workspace%20with%20coffee%20and%20notebook%2C%20cream%20walls%20with%20soft%20shadows%2C%20learning%20and%20education%20atmosphere%2C%20clean%20minimal%20design%2C%20terracotta%20and%20beige%20tones%2C%20editorial%20photography%2C%20focused%20and%20productive%20mood&width=1200&height=600&seq=come-funziona-hero&orientation=landscape',
      author: { '@type': 'Organization', name: 'Itaobuy Team', url: 'https://itaobuyspread.top' },
      publisher: { '@id': 'https://itaobuyspread.top/#organization' },
      mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://itaobuyspread.top/come-funziona-itaobuy-spreadsheet' },
      datePublished: '2026-07-14',
      dateModified: '2026-07-14',
      inLanguage: 'it',
      isPartOf: { '@id': 'https://itaobuyspread.top/#website' },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://itaobuyspread.top' },
        { '@type': 'ListItem', position: 2, name: 'Come Funziona', item: 'https://itaobuyspread.top/come-funziona-itaobuy-spreadsheet' },
      ],
    },
  ],
};

export default function ComeFunzionaPage() {
  return (
    <>
      <Navbar />
      <main>
        <article>
          <header className="pt-32 pb-16 md:pt-40 md:pb-20 px-6 md:px-10 bg-background-50">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100/80 text-accent-800 text-xs font-semibold tracking-wide uppercase mb-5">
                Tutorial
              </span>
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-tight mb-6">
                Come Funziona Itaobuy Spreadsheet: Guida Passo Dopo Passo
              </h1>
              <div className="flex items-center justify-center gap-4 text-sm text-foreground-500">
                <span>Da Itaobuy Team</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>Aggiornato: Luglio 2026</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>8 min di lettura</span>
              </div>
            </div>
          </header>

          <div className="max-w-4xl mx-auto px-6 md:px-10">
            <div className="mb-12 overflow-hidden rounded-[32px]">
              <img
                src="https://readdy.ai/api/search-image?query=Step%20by%20step%20tutorial%20concept%20on%20laptop%20screen%2C%20warm%20natural%20lighting%2C%20hands%20typing%20on%20keyboard%2C%20organized%20modern%20Italian%20workspace%20with%20coffee%20and%20notebook%2C%20cream%20walls%20with%20soft%20shadows%2C%20learning%20and%20education%20atmosphere%2C%20clean%20minimal%20design%2C%20terracotta%20and%20beige%20tones%2C%20editorial%20photography%2C%20focused%20and%20productive%20mood&width=1200&height=600&seq=come-funziona-hero&orientation=landscape"
                alt="Come funziona Itaobuy Spreadsheet tutorial"
                title="Itaobuy Spreadsheet - Tutorial Passo Dopo Passo"
                className="w-full aspect-[2/1] object-cover object-top"
              />
            </div>

            <div className="prose prose-lg max-w-none mb-16">
              <section className="mb-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  Introduzione
                </h2>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  Itaobuy Spreadsheet &egrave; uno strumento sorprendentemente semplice da usare, anche se non hai mai utilizzato un foglio di calcolo prima. In questa guida ti accompagneremo <strong>passo dopo passo</strong> attraverso ogni fase, dalla prima apertura fino all&apos;acquisto finale.
                </p>
                <p className="text-foreground-700 leading-relaxed">
                  <Link to="/" className="text-primary-600 font-semibold hover:text-primary-700 underline underline-offset-4">
                    Torna alla homepage di Itaobuy Spreadsheet
                  </Link> per una panoramica generale.
                </p>
              </section>

              <div className="space-y-8 mb-14">
                {[
                  { step: 1, title: 'Accedi allo Spreadsheet', desc: 'Visita il sito e apri il foglio di calcolo principale. Puoi farlo da qualsiasi dispositivo: computer, tablet o smartphone. Lo spreadsheet si carica direttamente nel browser, senza bisogno di installare software aggiuntivo. L&apos;interfaccia &egrave; pulita e intuitiva, con le categorie ben visibili nella parte superiore.' },
                  { step: 2, title: 'Scegli una Categoria', desc: 'Lo spreadsheet &egrave; organizzato per categorie di prodotto. Scegli tra Scarpe, Abbigliamento, Accessori e molto altro. Ogni categoria ha una scheda dedicata con tutti i prodotti disponibili. Puoi passare da una categoria all&apos;altra con un semplice click.' },
                  { step: 3, title: 'Analizza i Dettagli del Prodotto', desc: 'Ogni riga contiene informazioni dettagliate: nome del prodotto, prezzo, valutazione del venditore, numero di ordini completati, tempi di spedizione stimati e link alle foto QC. Prenditi il tempo di leggere tutte le colonne prima di decidere.' },
                  { step: 4, title: 'Confronta le Opzioni', desc: 'La struttura tabellare ti permette di confrontare facilmente prodotti simili. Guarda prezzi, valutazioni e foto QC fianco a fianco per identificare l&apos;opzione migliore per le tue esigenze.' },
                  { step: 5, title: 'Procedi all&apos;Acquisto', desc: 'Una volta scelto il prodotto, clicca sul link diretto fornito nello spreadsheet. Verrai reindirizzato alla pagina del prodotto dove potrai completare l&apos;ordine in pochi click, con la sicurezza di aver fatto una scelta informata.' },
                ].map((s) => (
                  <div key={s.step} className="flex gap-5 p-6 rounded-2xl bg-background-50 border border-background-200/60">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-primary-600 font-bold text-lg">{s.step}</span>
                    </div>
                    <div>
                      <h3 className="font-heading text-xl font-bold text-foreground-950 mb-2">{s.title}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <section className="mb-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  Consigli per un Utilizzo Efficiente
                </h2>
                <ul className="list-disc list-inside space-y-2 text-foreground-700 leading-relaxed mb-4 ml-2">
                  <li><strong>Usa i filtri</strong>: Lo spreadsheet supporta filtri per colonna. Usali per ordinare per prezzo, valutazione o data di aggiornamento.</li>
                  <li><strong>Salva i preferiti</strong>: Crea una copia personale dello spreadsheet per annotare i prodotti che ti interessano di pi&ugrave;.</li>
                  <li><strong>Controlla la data di aggiornamento</strong>: I prodotti vengono aggiornati settimanalmente. Verifica sempre che i dati siano recenti.</li>
                  <li><strong>Partecipa alla community</strong>: Condividi le tue esperienze per aiutare altri acquirenti italiani.</li>
                </ul>
              </section>

              <section className="mb-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  Conclusione
                </h2>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  Come hai visto, Itaobuy Spreadsheet &egrave; uno strumento <strong>estremamente semplice</strong> da utilizzare. In pochi minuti puoi passare da zero a fare acquisti informati e sicuri.
                </p>
                <p className="text-foreground-700 leading-relaxed">
                  <Link to="/" className="text-primary-600 font-semibold hover:text-primary-700 underline underline-offset-4">
                    Visita la homepage di Itaobuy Spreadsheet
                  </Link> per iniziare subito.
                </p>
              </section>
            </div>

            <div className="mb-20 p-8 rounded-[28px] bg-primary-500 text-center">
              <h3 className="font-heading text-2xl font-bold text-background-50 mb-3">
                Pronto a Iniziare?
              </h3>
              <p className="text-primary-100 mb-6 max-w-lg mx-auto">
                Ora che sai come funziona, accedi allo spreadsheet e fai il tuo primo acquisto.
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-background-50 text-sm font-semibold text-primary-600 hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap"
              >
                Itaobuy Sheet
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(comeFunzionaJsonLd),
            }}
          />
        </article>
      </main>
      <Footer />
    </>
  );
}